/**
 * Procedural pixel art asset generator for SURGGIES.
 * Generates all game sprites as canvas ImageData / offscreen canvases.
 * Miami Beach theme: sand, ocean, neon, palm trees, department buildings.
 */

// ── Color Palette ──────────────────────────────────────────────────
export const COLORS = {
  // Environment
  sand: "#F4D03F",
  sandDark: "#D4AC0D",
  ocean: "#2E86C1",
  oceanLight: "#5DADE2",
  oceanDark: "#1B4F72",
  boardwalk: "#8B6914",
  boardwalkLight: "#A67C00",
  grass: "#27AE60",
  grassDark: "#1E8449",
  pool: "#85C1E9",
  poolEdge: "#5DADE2",

  // Buildings
  wall: "#566573",
  wallLight: "#7F8C8D",
  door: "#4A235A",
  doorFrame: "#6C3483",
  window: "#85C1E9",
  roof: "#2C3E50",

  // Department colors
  marketing: "#FF6B9D",
  development: "#00D4FF",
  finance: "#00C853",
  sales: "#FF8C42",
  operations: "#9B59B6",
  hr: "#F39C12",
  support: "#1ABC9C",
  trading: "#E74C3C",

  // Props
  palmTrunk: "#6E2C00",
  palmLeaves: "#1E8449",
  neonPink: "#FF6B9D",
  neonBlue: "#00D4FF",
  neonOrange: "#FF8C42",

  // Characters
  skin: "#FDBCB4",
  skinDark: "#D4A490",
  hair: "#2C3E50",
  shirt: "#3498DB",
  pants: "#2C3E50",

  // Teams
  surgeSquad: "#FF6B35",
  pulseCrew: "#FFD700",
  wavePack: "#00D4FF",

  // UI
  darkBg: "#0A0E27",
  cardBg: "#151937",
  white: "#FFFFFF",
  black: "#000000",
} as const;

type PixelGrid = string[][];

function createPixelCanvas(width: number, height: number): OffscreenCanvas {
  const canvas = new OffscreenCanvas(width, height);
  return canvas;
}

function drawPixelGrid(
  ctx: OffscreenCanvasRenderingContext2D,
  grid: PixelGrid,
  pixelSize: number = 1,
  offsetX: number = 0,
  offsetY: number = 0
) {
  for (let y = 0; y < grid.length; y++) {
    for (let x = 0; x < grid[y].length; x++) {
      if (grid[y][x] && grid[y][x] !== "") {
        ctx.fillStyle = grid[y][x];
        ctx.fillRect(
          offsetX + x * pixelSize,
          offsetY + y * pixelSize,
          pixelSize,
          pixelSize
        );
      }
    }
  }
}

// ── Tile Generators (16x16) ────────────────────────────────────────

export function generateSandTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = COLORS.sand;
  ctx.fillRect(0, 0, 16, 16);
  // Add some grain
  ctx.fillStyle = COLORS.sandDark;
  for (let i = 0; i < 8; i++) {
    const x = Math.floor(Math.random() * 16);
    const y = Math.floor(Math.random() * 16);
    ctx.fillRect(x, y, 1, 1);
  }
  return canvas;
}

export function generateOceanTile(frame: number = 0): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = COLORS.ocean;
  ctx.fillRect(0, 0, 16, 16);
  // Animated wave lines
  ctx.fillStyle = COLORS.oceanLight;
  const waveOffset = frame % 8;
  for (let x = 0; x < 16; x++) {
    const y = 4 + Math.round(Math.sin((x + waveOffset) * 0.8) * 2);
    ctx.fillRect(x, y, 1, 1);
    const y2 = 10 + Math.round(Math.sin((x + waveOffset + 4) * 0.8) * 2);
    ctx.fillRect(x, y2, 1, 1);
  }
  return canvas;
}

export function generateBoardwalkTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = COLORS.boardwalk;
  ctx.fillRect(0, 0, 16, 16);
  // Wood plank lines
  ctx.fillStyle = COLORS.boardwalkLight;
  for (let y = 0; y < 16; y += 4) {
    ctx.fillRect(0, y, 16, 1);
  }
  // Gaps between planks
  ctx.fillStyle = COLORS.sandDark;
  ctx.fillRect(7, 0, 1, 16);
  return canvas;
}

export function generateGrassTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  // Richer base green
  ctx.fillStyle = "#4A9E4A";
  ctx.fillRect(0, 0, 16, 16);
  // Lighter blade strokes
  ctx.fillStyle = "#5CB85C";
  for (const [x, y] of [
    [1,0],[4,2],[7,1],[10,3],[13,0],[2,5],[6,4],[9,6],[14,2],[0,8],
    [3,9],[5,7],[8,11],[11,8],[15,5],[1,12],[4,14],[7,10],[12,13],[15,11],
    [2,15],[6,15],[10,14],[14,15],[0,3],[13,7],[9,2],[5,13],[11,1],[3,6],
  ]) { ctx.fillRect(x, y, 1, 1); }
  // Darker blade strokes for depth
  ctx.fillStyle = "#3A803A";
  for (const [x, y] of [
    [0,1],[3,3],[6,2],[9,0],[12,4],[15,1],[1,6],[4,8],[7,5],[10,7],
    [13,9],[2,10],[5,12],[8,14],[11,11],[14,13],[0,14],[3,15],[7,13],[15,9],
    [6,9],[9,12],[12,6],[1,4],[4,11],[10,15],[13,3],[2,13],[8,3],[5,1],
  ]) { ctx.fillRect(x, y, 1, 1); }
  // Tiny dark specks for pebbles/shadows
  ctx.fillStyle = "#2A6A2A";
  for (const [x, y] of [[5,5],[11,3],[2,9],[14,8],[7,14]]) {
    ctx.fillRect(x, y, 1, 1);
  }
  return canvas;
}

export function generatePathTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  // Warm grey base
  ctx.fillStyle = "#B0A090";
  ctx.fillRect(0, 0, 16, 16);
  // Cobblestone shapes — darker outlines
  ctx.fillStyle = "#8A7A6A";
  // Top-left stone outline
  ctx.fillRect(0, 0, 7, 1);
  ctx.fillRect(0, 0, 1, 7);
  ctx.fillRect(6, 0, 1, 7);
  ctx.fillRect(0, 6, 7, 1);
  // Top-right stone outline
  ctx.fillRect(8, 0, 8, 1);
  ctx.fillRect(8, 0, 1, 7);
  ctx.fillRect(15, 0, 1, 7);
  ctx.fillRect(8, 6, 8, 1);
  // Bottom-left stone outline
  ctx.fillRect(0, 8, 7, 1);
  ctx.fillRect(0, 8, 1, 8);
  ctx.fillRect(6, 8, 1, 8);
  ctx.fillRect(0, 15, 7, 1);
  // Bottom-right stone outline
  ctx.fillRect(8, 8, 8, 1);
  ctx.fillRect(8, 8, 1, 8);
  ctx.fillRect(15, 8, 1, 8);
  ctx.fillRect(8, 15, 8, 1);
  // Stone fill variations
  ctx.fillStyle = "#C0B0A0";
  ctx.fillRect(1, 1, 5, 5);
  ctx.fillRect(9, 9, 6, 6);
  ctx.fillStyle = "#A09080";
  ctx.fillRect(9, 1, 6, 5);
  ctx.fillRect(1, 9, 5, 6);
  // Slight highlight on top-left stones
  ctx.fillStyle = "#CCC0B0";
  ctx.fillRect(1, 1, 2, 1);
  ctx.fillRect(9, 1, 2, 1);
  return canvas;
}

export function generateFlowerPatchTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  // Same green base as grass
  ctx.fillStyle = "#4A9E4A";
  ctx.fillRect(0, 0, 16, 16);
  // Grass texture (lighter/darker blades)
  ctx.fillStyle = "#5CB85C";
  for (const [x, y] of [[2,1],[6,3],[10,1],[14,3],[1,7],[5,9],[9,7],[13,9],[3,13],[7,11],[11,13],[15,11]]) {
    ctx.fillRect(x, y, 1, 1);
  }
  ctx.fillStyle = "#3A803A";
  for (const [x, y] of [[0,2],[4,4],[8,2],[12,4],[2,8],[6,10],[10,8],[14,10],[1,14],[5,12],[9,14],[13,12]]) {
    ctx.fillRect(x, y, 1, 1);
  }
  // Flowers: stem pixel + bloom
  // White flower at (3,4)
  ctx.fillStyle = "#3A803A";
  ctx.fillRect(3, 5, 1, 1); // stem
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(2, 4, 3, 1);
  ctx.fillRect(3, 3, 1, 3);
  // Yellow flower at (10,6)
  ctx.fillStyle = "#3A803A";
  ctx.fillRect(10, 7, 1, 1);
  ctx.fillStyle = "#FFE040";
  ctx.fillRect(9, 6, 3, 1);
  ctx.fillRect(10, 5, 1, 3);
  // Pink flower at (6,12)
  ctx.fillStyle = "#3A803A";
  ctx.fillRect(6, 13, 1, 1);
  ctx.fillStyle = "#FF9EC4";
  ctx.fillRect(5, 12, 3, 1);
  ctx.fillRect(6, 11, 1, 3);
  // White flower at (13,2)
  ctx.fillStyle = "#3A803A";
  ctx.fillRect(13, 3, 1, 1);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(12, 2, 3, 1);
  ctx.fillRect(13, 1, 1, 3);
  return canvas;
}

export function generateDarkGrassTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  // Slightly darker base
  ctx.fillStyle = "#3A8B3A";
  ctx.fillRect(0, 0, 16, 16);
  // Lighter variations
  ctx.fillStyle = "#4AA04A";
  for (const [x, y] of [
    [0,1],[4,0],[8,3],[12,1],[15,4],[2,7],[6,5],[10,8],[14,6],[1,11],
    [5,10],[9,13],[13,11],[3,14],[7,15],[11,14],[0,5],[15,12],
  ]) { ctx.fillRect(x, y, 1, 1); }
  // Darker variations
  ctx.fillStyle = "#2A6A2A";
  for (const [x, y] of [
    [1,2],[5,4],[9,1],[13,3],[3,8],[7,6],[11,9],[15,7],[2,12],
    [6,14],[10,11],[14,13],[0,15],[4,13],[8,15],[12,9],
  ]) { ctx.fillRect(x, y, 1, 1); }
  return canvas;
}

export function generateTreeCanopyTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  // Dark outer ring
  ctx.fillStyle = "#2D6A1F";
  ctx.fillRect(0, 0, 16, 16);
  // Inner bright green mass
  ctx.fillStyle = "#4CAF50";
  ctx.fillRect(2, 2, 12, 12);
  // Brighter inner core
  ctx.fillStyle = "#5DBF52";
  ctx.fillRect(4, 4, 8, 8);
  // Highlight spot upper-left
  ctx.fillStyle = "#6FCF42";
  ctx.fillRect(3, 3, 4, 3);
  ctx.fillRect(3, 3, 3, 4);
  // Shadow spot lower-right
  ctx.fillStyle = "#1E5A15";
  ctx.fillRect(10, 10, 4, 4);
  ctx.fillRect(9, 12, 5, 2);
  ctx.fillRect(12, 9, 2, 5);
  // Dark corner pixels for roundness
  ctx.fillStyle = "#2D6A1F";
  ctx.fillRect(2, 2, 1, 1);
  ctx.fillRect(13, 2, 1, 1);
  ctx.fillRect(2, 13, 1, 1);
  ctx.fillRect(13, 13, 1, 1);
  return canvas;
}

export function generateRockTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  // Transparent (grass shows through around rock)
  ctx.clearRect(0, 0, 16, 16);
  // Rock base — slightly rounded shape
  ctx.fillStyle = "#9E9E9E";
  ctx.fillRect(3, 4, 10, 9);
  ctx.fillRect(4, 3, 8, 11);
  ctx.fillRect(5, 2, 6, 13);
  // White highlight top-left
  ctx.fillStyle = "#E0E0E0";
  ctx.fillRect(5, 3, 4, 2);
  ctx.fillRect(4, 4, 3, 3);
  // Dark shadow bottom-right
  ctx.fillStyle = "#616161";
  ctx.fillRect(9, 10, 4, 3);
  ctx.fillRect(10, 9, 3, 4);
  ctx.fillRect(8, 12, 5, 1);
  // Mid-tone body detail
  ctx.fillStyle = "#BDBDBD";
  ctx.fillRect(6, 6, 5, 4);
  return canvas;
}

export function generateFloorTile(color: string): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 16, 16);
  // Tile pattern
  ctx.fillStyle = "rgba(255,255,255,0.1)";
  ctx.fillRect(0, 0, 8, 8);
  ctx.fillRect(8, 8, 8, 8);
  ctx.fillStyle = "rgba(0,0,0,0.1)";
  ctx.fillRect(0, 0, 16, 1);
  ctx.fillRect(0, 0, 1, 16);
  return canvas;
}

export function generateWallTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = COLORS.wall;
  ctx.fillRect(0, 0, 16, 16);
  ctx.fillStyle = COLORS.wallLight;
  // Brick pattern
  for (let y = 0; y < 16; y += 4) {
    ctx.fillRect(0, y, 16, 1);
    const offset = (y / 4) % 2 === 0 ? 0 : 8;
    ctx.fillRect(offset, y, 1, 4);
    ctx.fillRect(offset + 8, y, 1, 4);
  }
  return canvas;
}

export function generateDoorTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  // Door frame
  ctx.fillStyle = COLORS.doorFrame;
  ctx.fillRect(0, 0, 16, 16);
  // Door
  ctx.fillStyle = COLORS.door;
  ctx.fillRect(2, 2, 12, 14);
  // Handle
  ctx.fillStyle = COLORS.neonPink;
  ctx.fillRect(10, 8, 2, 2);
  // Welcome mat
  ctx.fillStyle = COLORS.neonOrange;
  ctx.fillRect(4, 14, 8, 2);
  return canvas;
}

export function generatePoolTile(): OffscreenCanvas {
  const canvas = createPixelCanvas(16, 16);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = COLORS.pool;
  ctx.fillRect(0, 0, 16, 16);
  ctx.fillStyle = COLORS.poolEdge;
  // Ripple effect
  ctx.fillRect(4, 6, 8, 1);
  ctx.fillRect(2, 10, 12, 1);
  return canvas;
}

// ── Building Generators ────────────────────────────────────────────

export function generateBuildingExterior(
  deptType: string,
  width: number = 10,
  height: number = 8
): OffscreenCanvas {
  const pw = width * 16;
  const ph = height * 16;
  const canvas = createPixelCanvas(pw, ph);
  const ctx = canvas.getContext("2d")!;

  const accent = (COLORS as Record<string, string>)[deptType] || COLORS.wall;

  // Derive lighter wall colour and darker shadow from accent
  const wallBg = "#E8EEF4";   // clean off-white concrete
  const wallShadow = "#C5CDD8";
  const roofColor = "#2C3E50";
  const roofEdge = "#1A252F";
  const trimColor = accent;

  // ── Main wall ──
  ctx.fillStyle = wallBg;
  ctx.fillRect(0, 20, pw, ph - 20);

  // ── Horizontal brick lines (every 4px) ──
  ctx.fillStyle = wallShadow;
  for (let y = 24; y < ph - 16; y += 4) {
    ctx.fillRect(0, y, pw, 1);
  }

  // ── Vertical brick stagger (every 8px, alternating rows) ──
  for (let y = 24; y < ph - 16; y += 8) {
    const offset = ((y / 8) % 2 === 0) ? 0 : 4;
    for (let x = offset; x < pw; x += 8) {
      ctx.fillRect(x, y, 1, 4);
    }
  }

  // ── Corner pillar highlights ──
  ctx.fillStyle = "#F5F8FA";
  ctx.fillRect(0, 20, 4, ph - 20);
  ctx.fillRect(pw - 4, 20, 4, ph - 20);

  // ── Roof parapet ──
  ctx.fillStyle = roofColor;
  ctx.fillRect(0, 0, pw, 20);
  // Battlement notches along roofline
  ctx.fillStyle = "#0A1628";
  for (let x = 4; x < pw - 4; x += 16) {
    ctx.fillRect(x, 0, 8, 8);
  }
  // Roof edge trim
  ctx.fillStyle = trimColor;
  ctx.fillRect(0, 18, pw, 4);
  // Slight lighter highlight at very top
  ctx.fillStyle = roofEdge;
  ctx.fillRect(0, 0, pw, 2);

  // ── Department colour band (awning strip under roof) ──
  ctx.fillStyle = accent;
  ctx.fillRect(0, 22, pw, 6);
  // Awning shadow
  ctx.fillStyle = "rgba(0,0,0,0.2)";
  ctx.fillRect(0, 27, pw, 2);

  // ── Windows ──
  const winCols = width - 2;
  const winRows = height - 3; // leave roof (1) + awning (0.5) + ground floor (1)
  const cellW = Math.floor((pw - 32) / winCols);
  const winW = cellW - 6;
  const winH = 12;

  for (let wy = 0; wy < winRows; wy++) {
    const winY = 32 + wy * 18;
    for (let wx = 0; wx < winCols; wx++) {
      const winX = 16 + wx * cellW;
      // Window frame (dark)
      ctx.fillStyle = "#3D4A5C";
      ctx.fillRect(winX - 1, winY - 1, winW + 2, winH + 2);
      // Glass (light blue)
      ctx.fillStyle = COLORS.window;
      ctx.fillRect(winX, winY, winW, winH);
      // Glare reflection
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.fillRect(winX + 1, winY + 1, Math.floor(winW * 0.4), 3);
      ctx.fillRect(winX + 1, winY + 1, 2, Math.floor(winH * 0.5));
      // Window divider
      ctx.fillStyle = "#3D4A5C";
      ctx.fillRect(winX + Math.floor(winW / 2), winY, 1, winH);
    }
  }

  // ── Ground floor accent strip ──
  ctx.fillStyle = accent + "CC";
  ctx.fillRect(0, ph - 24, pw, 24);
  ctx.fillStyle = "rgba(0,0,0,0.15)";
  ctx.fillRect(0, ph - 24, pw, 2);

  // ── Door ──
  const doorW = 20;
  const doorH = 22;
  const doorX = Math.floor(pw / 2) - Math.floor(doorW / 2);
  const doorY = ph - doorH - 1;

  // Door awning
  ctx.fillStyle = accent;
  ctx.fillRect(doorX - 8, ph - doorH - 8, doorW + 16, 6);
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.fillRect(doorX - 8, ph - doorH - 3, doorW + 16, 2);

  // Door frame
  ctx.fillStyle = "#1A252F";
  ctx.fillRect(doorX - 2, doorY - 1, doorW + 4, doorH + 2);
  // Door panels
  ctx.fillStyle = "#2C3E50";
  ctx.fillRect(doorX, doorY, doorW, doorH);
  // Door glass panes
  ctx.fillStyle = COLORS.window;
  ctx.fillRect(doorX + 2, doorY + 2, 7, 9);
  ctx.fillRect(doorX + 11, doorY + 2, 7, 9);
  // Door handle
  ctx.fillStyle = "#F0C030";
  ctx.fillRect(doorX + 9, doorY + 13, 2, 3);

  // ── Sign above awning ──
  const signY = 6;
  const signH = 8;
  ctx.fillStyle = "rgba(0,0,0,0.75)";
  ctx.fillRect(16, signY - 1, pw - 32, signH + 2);
  ctx.fillStyle = accent;
  ctx.fillRect(16, signY, pw - 32, signH);
  // Subtle neon glow
  ctx.fillStyle = "rgba(255,255,255,0.2)";
  ctx.fillRect(17, signY, pw - 34, 2);

  return canvas;
}

// ── Prop Generators ────────────────────────────────────────────────

export function generatePalmTree(): OffscreenCanvas {
  const canvas = createPixelCanvas(32, 48);
  const ctx = canvas.getContext("2d")!;

  // Trunk
  ctx.fillStyle = COLORS.palmTrunk;
  ctx.fillRect(14, 16, 4, 32);
  ctx.fillRect(13, 20, 6, 4);

  // Leaves (top)
  ctx.fillStyle = COLORS.palmLeaves;
  // Center tuft
  ctx.fillRect(12, 4, 8, 4);
  ctx.fillRect(10, 6, 12, 4);
  // Left frond
  ctx.fillRect(2, 8, 12, 3);
  ctx.fillRect(0, 10, 8, 2);
  // Right frond
  ctx.fillRect(18, 8, 12, 3);
  ctx.fillRect(24, 10, 8, 2);
  // Top frond
  ctx.fillRect(13, 0, 6, 6);
  ctx.fillRect(11, 2, 10, 3);

  return canvas;
}

export function generateTikiBar(): OffscreenCanvas {
  const canvas = createPixelCanvas(48, 32);
  const ctx = canvas.getContext("2d")!;

  // Bar counter
  ctx.fillStyle = COLORS.boardwalk;
  ctx.fillRect(0, 16, 48, 16);

  // Tiki roof
  ctx.fillStyle = COLORS.palmLeaves;
  ctx.fillRect(0, 0, 48, 8);
  ctx.fillStyle = COLORS.palmTrunk;
  ctx.fillRect(0, 8, 48, 4);
  // Fringe
  for (let x = 0; x < 48; x += 4) {
    ctx.fillStyle = COLORS.palmLeaves;
    ctx.fillRect(x, 12, 2, 4);
  }

  // Neon sign
  ctx.fillStyle = COLORS.neonPink;
  ctx.fillRect(12, 2, 24, 4);

  // Bottles
  ctx.fillStyle = COLORS.neonBlue;
  ctx.fillRect(8, 18, 4, 8);
  ctx.fillStyle = COLORS.neonOrange;
  ctx.fillRect(16, 18, 4, 8);
  ctx.fillStyle = COLORS.neonPink;
  ctx.fillRect(24, 18, 4, 8);

  return canvas;
}

// ── Character Generators (16x32, 4 frames x 4 directions) ─────────

export function generateCharacterSprite(
  shirtColor: string,
  pantsColor: string,
  hairColor: string = COLORS.hair,
  isNpc: boolean = false
): OffscreenCanvas {
  // 4 columns (frames) x 4 rows (directions: down, left, right, up)
  const canvas = createPixelCanvas(16 * 4, 32 * 4);
  const ctx = canvas.getContext("2d")!;

  for (let dir = 0; dir < 4; dir++) {
    for (let frame = 0; frame < 4; frame++) {
      const ox = frame * 16;
      const oy = dir * 32;
      const walkOffset = frame % 2 === 0 ? 0 : 1;

      // Hair/Head top
      ctx.fillStyle = hairColor;
      ctx.fillRect(ox + 4, oy + 0, 8, 4);
      ctx.fillRect(ox + 3, oy + 2, 10, 3);

      // Face
      ctx.fillStyle = COLORS.skin;
      ctx.fillRect(ox + 4, oy + 4, 8, 6);
      ctx.fillRect(ox + 5, oy + 3, 6, 2);

      // Eyes (direction-based)
      ctx.fillStyle = COLORS.black;
      if (dir === 0) {
        // Down
        ctx.fillRect(ox + 6, oy + 6, 1, 2);
        ctx.fillRect(ox + 9, oy + 6, 1, 2);
      } else if (dir === 1) {
        // Left
        ctx.fillRect(ox + 5, oy + 6, 1, 2);
        ctx.fillRect(ox + 8, oy + 6, 1, 2);
      } else if (dir === 2) {
        // Right
        ctx.fillRect(ox + 7, oy + 6, 1, 2);
        ctx.fillRect(ox + 10, oy + 6, 1, 2);
      } else {
        // Up — no eyes visible
      }

      // Body/Shirt
      ctx.fillStyle = shirtColor;
      ctx.fillRect(ox + 3, oy + 10, 10, 8);
      // Arms
      ctx.fillRect(ox + 1, oy + 10 + walkOffset, 2, 6);
      ctx.fillRect(ox + 13, oy + 10 + (walkOffset ? 0 : 1), 2, 6);

      // Pants
      ctx.fillStyle = pantsColor;
      ctx.fillRect(ox + 4, oy + 18, 8, 6);

      // Legs (animated)
      ctx.fillRect(ox + 4 + walkOffset, oy + 24, 3, 6);
      ctx.fillRect(ox + 9 - walkOffset, oy + 24, 3, 6);

      // Shoes
      ctx.fillStyle = COLORS.black;
      ctx.fillRect(ox + 3 + walkOffset, oy + 30, 4, 2);
      ctx.fillRect(ox + 9 - walkOffset, oy + 30, 4, 2);

      // NPC indicator (small colored dot above head)
      if (isNpc) {
        ctx.fillStyle = shirtColor;
        ctx.fillRect(ox + 7, oy - 1, 2, 2);
      }
    }
  }

  return canvas;
}

// ── Neon Sign Generator ────────────────────────────────────────────

export function generateNeonSign(
  text: string,
  color: string = COLORS.neonPink
): OffscreenCanvas {
  const charWidth = 6;
  const width = text.length * charWidth + 8;
  const canvas = createPixelCanvas(width, 16);
  const ctx = canvas.getContext("2d")!;

  // Background
  ctx.fillStyle = COLORS.darkBg;
  ctx.fillRect(0, 0, width, 16);

  // Border glow
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, 1);
  ctx.fillRect(0, 15, width, 1);
  ctx.fillRect(0, 0, 1, 16);
  ctx.fillRect(width - 1, 0, 1, 16);

  // Text (simplified pixel font — just colored blocks)
  ctx.fillStyle = color;
  ctx.font = "8px monospace";
  ctx.fillText(text, 4, 11);

  return canvas;
}

// ── Master Asset Map ───────────────────────────────────────────────

export interface GeneratedAssets {
  tiles: {
    sand: OffscreenCanvas;
    ocean: OffscreenCanvas[];
    boardwalk: OffscreenCanvas;
    grass: OffscreenCanvas;
    wall: OffscreenCanvas;
    door: OffscreenCanvas;
    pool: OffscreenCanvas;
    floors: Record<string, OffscreenCanvas>;
    path: OffscreenCanvas;
    flowerPatch: OffscreenCanvas;
    darkGrass: OffscreenCanvas;
    treeCanopy: OffscreenCanvas;
    rock: OffscreenCanvas;
  };
  buildings: Record<string, OffscreenCanvas>;
  props: {
    palmTree: OffscreenCanvas;
    tikiBar: OffscreenCanvas;
  };
  characters: {
    ceo_1: OffscreenCanvas;
    ceo_2: OffscreenCanvas;
    ceo_3: OffscreenCanvas;
    ceo_4: OffscreenCanvas;
    ceo_5: OffscreenCanvas;
    ceo_6: OffscreenCanvas;
    surggie_surge_1: OffscreenCanvas;
    surggie_surge_2: OffscreenCanvas;
    surggie_surge_3: OffscreenCanvas;
    surggie_pulse_1: OffscreenCanvas;
    surggie_pulse_2: OffscreenCanvas;
    surggie_pulse_3: OffscreenCanvas;
    surggie_wave_1: OffscreenCanvas;
    surggie_wave_2: OffscreenCanvas;
    surggie_wave_3: OffscreenCanvas;
  };
  signs: Record<string, OffscreenCanvas>;
}

export function generateAllAssets(): GeneratedAssets {
  // Ocean frames for animation
  const oceanFrames: OffscreenCanvas[] = [];
  for (let i = 0; i < 8; i++) {
    oceanFrames.push(generateOceanTile(i));
  }

  // Department floor tiles
  const deptTypes = [
    "marketing", "development", "finance", "sales",
    "operations", "hr", "support", "trading",
  ];
  const floors: Record<string, OffscreenCanvas> = {};
  const buildings: Record<string, OffscreenCanvas> = {};
  const signs: Record<string, OffscreenCanvas> = {};

  for (const type of deptTypes) {
    const color = (COLORS as Record<string, string>)[type] || COLORS.wall;
    // Lighten color for floor
    floors[type] = generateFloorTile(color + "40");
    buildings[type] = generateBuildingExterior(type, 10, 8);
    signs[type] = generateNeonSign(type.toUpperCase(), color);
  }

  return {
    tiles: {
      sand: generateSandTile(),
      ocean: oceanFrames,
      boardwalk: generateBoardwalkTile(),
      grass: generateGrassTile(),
      wall: generateWallTile(),
      door: generateDoorTile(),
      pool: generatePoolTile(),
      floors,
      path: generatePathTile(),
      flowerPatch: generateFlowerPatchTile(),
      darkGrass: generateDarkGrassTile(),
      treeCanopy: generateTreeCanopyTile(),
      rock: generateRockTile(),
    },
    buildings,
    props: {
      palmTree: generatePalmTree(),
      tikiBar: generateTikiBar(),
    },
    characters: {
      // CEO avatars (6 variants)
      ceo_1: generateCharacterSprite("#3498DB", COLORS.pants),
      ceo_2: generateCharacterSprite("#E74C3C", "#1A1A2E"),
      ceo_3: generateCharacterSprite("#27AE60", "#2C3E50"),
      ceo_4: generateCharacterSprite("#F39C12", "#34495E"),
      ceo_5: generateCharacterSprite("#9B59B6", "#1A1A2E"),
      ceo_6: generateCharacterSprite("#FF6B9D", "#2C3E50"),
      // Surge Squad Surggies (🔥 warm colors)
      surggie_surge_1: generateCharacterSprite(COLORS.surgeSquad, "#8B2500", "#FF4500", true),
      surggie_surge_2: generateCharacterSprite("#FF8C42", "#6B2000", "#CC3700", true),
      surggie_surge_3: generateCharacterSprite("#FFB347", "#5C1A00", "#B33000", true),
      // Pulse Crew Surggies (⚡ gold/yellow)
      surggie_pulse_1: generateCharacterSprite(COLORS.pulseCrew, "#8B7500", "#DAA520", true),
      surggie_pulse_2: generateCharacterSprite("#FFEC8B", "#6B5B00", "#B8860B", true),
      surggie_pulse_3: generateCharacterSprite("#FFD700", "#5C4E00", "#A0850B", true),
      // Wave Pack Surggies (🌊 blue/cyan)
      surggie_wave_1: generateCharacterSprite(COLORS.wavePack, "#004466", "#006699", true),
      surggie_wave_2: generateCharacterSprite("#00B4D8", "#003D5C", "#005577", true),
      surggie_wave_3: generateCharacterSprite("#48CAE4", "#003050", "#004C70", true),
    },
    signs,
  };
}

const SPECIES_TYPE_COLORS: Record<number, string> = {
  1: "#FF6B9D", 2: "#FF4580",  // Salespup, Pitchdog (sales)
  3: "#00D4FF", 4: "#00B8E0",  // Byte, Debuggor (tech)
  5: "#00C853", 6: "#009E3D",  // Qubit, Stonkzilla (finance)
  7: "#FFD700", 8: "#E6BE00",  // Vibecat, Brandlion (creative)
  9: "#FF8C42", 10: "#E67320", // Opster, Sysmonk (ops)
  11: "#9B59B6", 12: "#7D3C98", // Cratus, Huskii (legendary)
};

const SPECIES_SHAPES: Record<number, "quad" | "round" | "tall" | "wide"> = {
  1: "quad", 2: "quad",
  3: "round", 4: "round",
  5: "tall", 6: "wide",
  7: "round", 8: "tall",
  9: "wide", 10: "quad",
  11: "quad", 12: "round",
};

export function generateSurggieSprite(speciesId: number): OffscreenCanvas {
  const canvas = new OffscreenCanvas(32, 32);
  const ctx = canvas.getContext("2d")!;
  const color = SPECIES_TYPE_COLORS[speciesId] ?? "#FF6B9D";
  const shape = SPECIES_SHAPES[speciesId] ?? "round";
  const dark = color + "99";

  ctx.fillStyle = color + "22";
  ctx.fillRect(0, 0, 32, 32);

  ctx.fillStyle = color;
  if (shape === "round") {
    ctx.beginPath();
    ctx.arc(16, 16, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = dark;
    ctx.beginPath();
    ctx.arc(13, 13, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(19, 13, 3, 0, Math.PI * 2);
    ctx.fill();
  } else if (shape === "tall") {
    ctx.fillRect(10, 6, 12, 20);
    ctx.fillStyle = dark;
    ctx.fillRect(12, 10, 3, 3);
    ctx.fillRect(17, 10, 3, 3);
  } else if (shape === "wide") {
    ctx.fillRect(4, 10, 24, 12);
    ctx.fillStyle = dark;
    ctx.fillRect(8, 13, 3, 3);
    ctx.fillRect(21, 13, 3, 3);
  } else {
    ctx.fillRect(8, 8, 16, 16);
    ctx.fillStyle = dark;
    ctx.fillRect(10, 11, 4, 4);
    ctx.fillRect(18, 11, 4, 4);
  }

  // level indicator dots for legendary
  if (speciesId >= 11) {
    ctx.fillStyle = "#FFD700";
    ctx.fillRect(4, 4, 2, 2);
    ctx.fillRect(26, 4, 2, 2);
  }

  return canvas;
}
